import React from "react";
import { cn } from "@/utils/shadcn";
import { NUM } from "./tokens";
import { Lab } from "./atoms";

/* ── Ring · SVG progress ring ───────────────────────────────────────── */

export const Ring = ({
  pct,
  size = 84,
  stroke = 8,
  label,
  className,
}: {
  /** 0–100 */
  pct: number;
  size?: number;
  stroke?: number;
  /** defaults to the rounded percentage */
  label?: React.ReactNode;
  className?: string;
}) => {
  const r = size / 2 - stroke / 2 - 1;
  const circumference = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(100, pct));
  return (
    <div
      className={cn("relative grid flex-none place-items-center", className)}
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke="var(--ms-card2)"
          strokeWidth={stroke}
          fill="none"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke="var(--ms-rule)"
          strokeWidth={stroke}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - clamped / 100)}
        />
      </svg>
      <span className={cn(NUM, "absolute text-[19px] font-semibold")}>
        {label ?? `${Math.round(clamped)}%`}
      </span>
    </div>
  );
};

/* ── Stage · one row of a running import job ────────────────────────── */

export const Stage = ({
  state,
  title,
  sub,
  progress,
  className,
}: {
  state: "done" | "live" | "wait";
  title: React.ReactNode;
  sub?: React.ReactNode;
  /** 0–100; renders the thin bar under the sub-line */
  progress?: number;
  className?: string;
}) => (
  <div className={cn("flex gap-3 border-t border-ms-line py-[13px] first:border-t-0", className)}>
    <span
      className={cn(
        "grid h-[22px] w-[22px] flex-none place-items-center rounded-full",
        state === "done" && "bg-ms-green text-ms-bg",
        state === "live" && "bg-ms-mint-soft",
        state === "wait" && "border-[1.5px] border-ms-line"
      )}
    >
      {state === "done" ? (
        <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
          <path
            d="M1.5 6.3l3 3L10.5 3"
            stroke="currentColor"
            strokeWidth="1.9"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : state === "live" ? (
        <span className="block h-2 w-2 animate-pulse rounded-full bg-ms-rule" />
      ) : null}
    </span>
    <div className="min-w-0 flex-1">
      <div className={cn("text-[14px] font-medium", state === "wait" && "text-ms-dim")}>
        {title}
      </div>
      {sub ? <Lab className="mt-[3px] block">{sub}</Lab> : null}
      {progress != null ? (
        <div className="mt-2 h-1 overflow-hidden border border-ms-line bg-ms-card2">
          <i
            className="block h-full bg-ms-rule"
            style={{ width: `${Math.max(0, Math.min(100, progress))}%` }}
          />
        </div>
      ) : null}
    </div>
  </div>
);
